import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { setToken } from "@/lib/session";
import { Trophy, Brain, ArrowRight, Shield, PlayCircle, Sparkles, Grid3x3, Type, HelpCircle, Camera, Pencil, Building2, Info, BarChart3 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const GAME_PREVIEW = [
  { icon: Brain,       label: "Rebus" },
  { icon: Grid3x3,     label: "Sudoku" },
  { icon: Type,        label: "Word" },
  { icon: HelpCircle,  label: "Trivia" },
  { icon: Camera,      label: "Caption" },
  { icon: Pencil,      label: "Pictionary" },
  { icon: Building2,   label: "Dept Battles" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [demoToken, setDemoToken] = useState("");

  useEffect(() => {
    api.get("/public/info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const goDemo = () => { setToken("demo"); navigate("/play"); };
  const goAdmin = () => navigate("/admin");
  const goReports = () => navigate("/reports");
  const goAts = () => {
    if (demoToken.trim()) { setToken(demoToken.trim()); navigate("/play"); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="border-b-4 border-black px-6 py-4 flex items-center justify-between">
        <BrandLogo size="md" title="TekWissen Fun Zone" subtitle="Where work meets play" />
        <div className="flex items-center gap-2">
          <button
            data-testid="header-about-link"
            onClick={() => navigate("/about")}
            className="brutal-btn-secondary text-sm flex items-center gap-2"
          >
            <Info size={16} /> About
          </button>
          <button
            data-testid="header-admin-link"
            onClick={goAdmin}
            className="brutal-btn-secondary text-sm flex items-center gap-2"
          >
            <Shield size={16} /> Admin Login
          </button>
          <button
            data-testid="header-reports-link"
            onClick={goReports}
            className="brutal-btn-secondary text-sm flex items-center gap-2"
          >
            <BarChart3 size={16} /> Reports Login
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block bg-[#FFD600] border-2 border-black px-3 py-1 brutal-shadow-sm mb-6">
              <span className="font-mono text-xs font-bold">
                DAILY · {info?.puzzle_count ?? "—"} PUZZLES READY · 7 GAMES LIVE
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter uppercase leading-none">
              The internal<br/>
              <span
                className="font-graffiti text-[#39FF14] inline-block rotate-[-3deg] text-6xl md:text-8xl"
                style={{ WebkitTextStroke: "2px black" }}
              >
                playground
              </span><br/>
              for TekWissen.
            </h1>
            <p className="text-lg mt-6 max-w-md text-neutral-700">
              Daily Rebus, Sudoku, Word Building, Trivia, Caption Contests, Pictionary
              and Department Battles. Solve, streak, climb the leaderboard.
            </p>

            {/* Primary CTAs */}
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="landing-try-demo-button"
                onClick={goDemo}
                className="brutal-btn flex items-center gap-2 text-base"
              >
                <PlayCircle size={20} /> Try Demo (No Token)
              </button>
              <button
                data-testid="landing-admin-cta-button"
                onClick={goAdmin}
                className="brutal-btn-secondary flex items-center gap-2 text-base"
              >
                <Shield size={20} /> Admin Login
              </button>
              <button
                data-testid="landing-reports-cta-button"
                onClick={goReports}
                className="brutal-btn-secondary flex items-center gap-2 text-base"
              >
                <BarChart3 size={20} /> Reports Login
              </button>
              <button
                data-testid="landing-about-button"
                onClick={() => navigate("/about")}
                className="brutal-btn-secondary flex items-center gap-2 text-base"
              >
                <Info size={20} /> About &amp; Rules
              </button>
            </div>

            {/* ATS Token Entry */}
            <div className="mt-8 border-2 border-dashed border-black p-4 max-w-md">
              <div className="text-xs font-black uppercase tracking-widest mb-2 flex items-center gap-1">
                <Sparkles size={12}/> Have an ATS invite?
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="landing-token-input"
                  value={demoToken}
                  onChange={(e) => setDemoToken(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && goAts()}
                  placeholder="Paste your invite token..."
                  className="brutal-input flex-1"
                />
                <button
                  data-testid="landing-enter-button"
                  onClick={goAts}
                  className="brutal-btn flex items-center gap-1 px-4"
                >
                  Enter <ArrowRight size={18} />
                </button>
              </div>
              <p className="text-xs mt-2 text-neutral-500">
                Tip: your ATS link auto-includes the token — you usually won't need to paste it.
              </p>
            </div>
          </div>

          {/* Mock puzzle card */}
          <div className="relative">
            <div className="puzzle-card p-8 md:p-12 relative">
              <div
                className="absolute -top-12 left-1/2 -translate-x-1/2 font-graffiti text-5xl text-[#39FF14] rotate-[-2deg]"
                style={{ WebkitTextStroke: "2px black" }}
              >
                guess this!
              </div>
              <div className="text-center text-5xl md:text-7xl font-black tracking-tighter">
                GOOD <span style={{ color: "#FFD600" }}>🐝</span><br/>
                GOOD <span style={{ color: "#FFD600" }}>🐝</span><br/>
                TRUE
              </div>
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#FFD600] border-2 border-black px-4 py-2 rotate-[3deg] brutal-shadow-sm">
              <span className="font-black uppercase text-sm">Hint: Idiom</span>
            </div>
          </div>
        </div>
      </section>

      {/* Game Strip */}
      <section className="border-t-4 border-black bg-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">
            7 games · refreshed daily
          </div>
          <div className="flex flex-wrap gap-3">
            {GAME_PREVIEW.map((g, i) => (
              <div
                key={i}
                className="flex items-center gap-2 border-2 border-black px-4 py-2 brutal-shadow-sm bg-[#FDFBF7]"
                data-testid={`landing-game-chip-${g.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <g.icon size={18} />
                <span className="font-black uppercase text-sm">{g.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t-4 border-black bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
          {[
            { icon: PlayCircle, color: "#39FF14", title: "Try It Instantly", desc: "Hit Try Demo above — explore all 7 games as a demo player, no sign-up." },
            { icon: Trophy,     color: "#FFD600", title: "Top 3 Win Daily", desc: "Fastest correct answers climb the podium. Weekly & monthly leaderboards too." },
            { icon: Shield,     color: "#FF3B30", title: "Admin Control",   desc: "Schedule puzzles, manage users, toggle games, view health metrics — all from /admin." },
          ].map((f, i) => (
            <div key={i} className="brutal-card p-6">
              <div
                className="w-12 h-12 border-2 border-black flex items-center justify-center mb-4"
                style={{ background: f.color }}
              >
                <f.icon size={24} className="text-black" />
              </div>
              <h3 className="text-2xl font-black uppercase tracking-tight">{f.title}</h3>
              <p className="mt-2 text-neutral-700">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t-4 border-black px-6 py-6 text-center text-sm text-neutral-600">
        TekWissen Employee Fun Zone · Built for in-house ATS integration · Drop the link, play.
      </footer>
    </div>
  );
}
